import React, { useState } from 'react'
import { Brain, Sparkles, TestTube, Zap, Target, TrendingUp, Users, Clock, Image } from 'lucide-react'
import { cn } from '@utils/cn'

const AITestPanel = ({ onTestAI }) => {
  const [selectedTest, setSelectedTest] = useState(null)

  const testScenarios = [
    {
      id: 'performance',
      title: 'Performance Analysis',
      description: 'Test AI performance analysis and insights',
      icon: TrendingUp,
      prompt: 'Analyze my performance',
      category: 'analytics'
    },
    {
      id: 'content',
      title: 'Content Creation',
      description: 'Test AI content creation suggestions',
      icon: Target,
      prompt: 'Create content for restaurant',
      category: 'content'
    },
    {
      id: 'optimization',
      title: 'System Optimization',
      description: 'Test AI optimization recommendations',
      icon: Zap,
      prompt: 'Optimize my system',
      category: 'optimization'
    },
    {
      id: 'troubleshoot',
      title: 'Troubleshooting',
      description: 'Test AI troubleshooting capabilities',
      icon: TestTube,
      prompt: 'Troubleshoot my system',
      category: 'support'
    },
    {
      id: 'recommendations',
      title: 'Smart Recommendations',
      description: 'Test AI-powered recommendations',
      icon: Brain,
      prompt: 'Show me optimization recommendations',
      category: 'ai'
    },
    {
      id: 'status',
      title: 'System Status',
      description: 'Test AI system status monitoring',
      icon: Users,
      prompt: 'Check system status',
      category: 'monitoring'
    },
    {
      id: 'schedule',
      title: 'Schedule Optimization',
      description: 'Test AI scheduling suggestions',
      icon: Clock,
      prompt: 'Optimize my schedule',
      category: 'scheduling'
    },
    {
      id: 'generate_image',
      title: 'Generate Image',
      description: 'Test AI image generation with DALL-E',
      icon: Sparkles,
      prompt: 'Generate a restaurant menu background',
      category: 'api'
    },
    {
      id: 'image_generation',
      title: 'Image Generation',
      description: 'Test Google AI image generation',
      icon: Image,
      prompt: 'Generate a modern digital signage background',
      category: 'api'
    },
    {
      id: 'help',
      title: 'AI Help',
      description: 'Test AI help and guidance',
      icon: Sparkles,
      prompt: 'What can you help me with?',
      category: 'support'
    }
  ]

  const handleTestClick = (test) => {
    setSelectedTest(test.id)
    onTestAI(test.prompt)
    // Reset selection after a short delay
    setTimeout(() => setSelectedTest(null), 1000)
  }

  const getCategoryColor = (category) => {
    const colors = {
      analytics: 'bg-blue-100 text-blue-700 border-blue-200',
      content: 'bg-green-100 text-green-700 border-green-200',
      optimization: 'bg-purple-100 text-purple-700 border-purple-200',
      support: 'bg-orange-100 text-orange-700 border-orange-200',
      ai: 'bg-pink-100 text-pink-700 border-pink-200',
      monitoring: 'bg-indigo-100 text-indigo-700 border-indigo-200',
      scheduling: 'bg-teal-100 text-teal-700 border-teal-200',
      api: 'bg-yellow-100 text-yellow-700 border-yellow-200'
    }
    return colors[category] || 'bg-gray-100 text-gray-700 border-gray-200'
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-blue-600 rounded-lg flex items-center justify-center">
          <Brain className="w-6 h-6 text-white" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-gray-900">AI Agent Test Panel</h2>
          <p className="text-sm text-gray-600">Test the enhanced AI capabilities</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {testScenarios.map((test) => (
          <button
            key={test.id}
            onClick={() => handleTestClick(test)}
            disabled={selectedTest === test.id}
            className={cn(
              "p-4 rounded-lg border-2 transition-all duration-200 text-left hover:scale-105",
              getCategoryColor(test.category),
              selectedTest === test.id && "ring-2 ring-purple-500 ring-offset-2",
              "disabled:opacity-50 disabled:cursor-not-allowed"
            )}
          >
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <test.icon className="w-6 h-6" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold mb-1">{test.title}</h3>
                <p className="text-sm opacity-80 mb-2">{test.description}</p>
                <div className="text-xs opacity-70 bg-white/20 rounded px-2 py-1 inline-block">
                  "{test.prompt}"
                </div>
              </div>
            </div>
          </button>
        ))}
      </div>

      <div className="mt-6 p-4 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border border-purple-200">
        <div className="flex items-center space-x-2 mb-2">
          <Sparkles className="w-5 h-5 text-purple-600" />
          <h3 className="font-semibold text-purple-900">AI Agent Features</h3>
        </div>
        <div className="grid grid-cols-2 gap-2 text-sm text-purple-800">
          <div>• Real-time data analysis</div>
          <div>• Performance scoring</div>
          <div>• Content suggestions</div>
          <div>• Optimization recommendations</div>
          <div>• Troubleshooting assistance</div>
          <div className="text-sm text-gray-600 space-y-1">
            <div>• Context-aware responses</div>
            <div>• AI image generation</div>
            <div>• API-powered features</div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AITestPanel 